import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Grid
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  History
} from '@mui/icons-material';
import { auditService } from '../../services/auditService';
import { DocumentAuditLog, AUDIT_ACTION_LABELS, AUDIT_STATUS_COLORS } from '../../types/audit';

interface DocumentAuditTrailProps {
  documentId: string;
  documentType: 'estimate' | 'sales_order' | 'invoice' | 'delivery_challan' | 'sales_return';
  defaultExpanded?: boolean;
}

const DocumentAuditTrail: React.FC<DocumentAuditTrailProps> = ({
  documentId,
  documentType,
  defaultExpanded = false
}) => {
  const [logs, setLogs] = useState<DocumentAuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  useEffect(() => {
    loadAuditLogs();
  }, [documentId, documentType]);

  const loadAuditLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditService.getDocumentAuditHistory(documentId, {
        document_type: documentType,
        limit: 100,
        offset: 0
      });
      setLogs(response.audit_logs);
    } catch (err: any) {
      console.error('Error loading audit logs:', err);
      setError(err.message || 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  const toggleLogExpansion = (logId: string) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionLabel = (action: string): string => {
    return AUDIT_ACTION_LABELS[action] || action;
  };

  const getStatusColor = (status: string) => {
    return AUDIT_STATUS_COLORS[status] || 'default';
  };

  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="audit-trail-content"
        id="audit-trail-header"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <History />
          <Typography variant="h6">Audit Trail</Typography>
          {logs.length > 0 && (
            <Chip
              label={`${logs.length} events`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : logs.length === 0 ? (
          <Alert severity="info">No audit events found for this document</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(log.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {log.actor_identifier || 'System'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.actor_type}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getActionLabel(log.action)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          color={getStatusColor(log.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {Object.keys(log.details).length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => toggleLogExpansion(log.id)}
                          >
                            {expandedLog === log.id ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {Object.keys(log.details).length > 0 && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ p: 0 }}>
                          <Collapse in={expandedLog === log.id} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Event Details
                              </Typography>
                              <Grid container spacing={2}>
                                {log.event_type && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">
                                      Event Type
                                    </Typography>
                                    <Typography variant="body2">{log.event_type}</Typography>
                                  </Grid>
                                )}
                                {log.event_category && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">
                                      Category
                                    </Typography>
                                    <Typography variant="body2">{log.event_category}</Typography>
                                  </Grid>
                                )}
                                {log.ip_address && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">
                                      IP Address
                                    </Typography>
                                    <Typography variant="body2">{log.ip_address}</Typography>
                                  </Grid>
                                )}
                                {log.request_id && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">
                                      Request ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                      {log.request_id}
                                    </Typography>
                                  </Grid>
                                )}
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary">
                                    Additional Details
                                  </Typography>
                                  <Paper variant="outlined" sx={{ p: 1, mt: 0.5, backgroundColor: '#fff' }}>
                                    <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto', maxHeight: '200px' }}>
                                      {JSON.stringify(log.details, null, 2)}
                                    </pre>
                                  </Paper>
                                </Grid>
                                {log.error_message && (
                                  <Grid item xs={12}>
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                      {log.error_message}
                                    </Alert>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default DocumentAuditTrail;
